import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({ baseURL: API });

// Attach the admin JWT (if present) automatically.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---- Public ----
export async function getHero() {
  const { data } = await api.get("/photographs/hero");
  return data;
}

export async function getEntry(excludeId) {
  const { data } = await api.get("/photographs/entry", {
    params: excludeId ? { exclude: excludeId } : {},
  });
  return data;
}

export async function getPhotograph(id) {
  const { data } = await api.get(`/photographs/${id}`);
  return data;
}

export async function listPhotographs() {
  const { data } = await api.get("/photographs");
  return data;
}

export async function getConnections(id) {
  const { data } = await api.get(`/photographs/${id}/connections`);
  return data;
}

export async function submitContact(payload) {
  const { data } = await api.post("/contact", payload);
  return data;
}

// ---- Admin auth ----
export async function adminLogin(email, password) {
  const { data } = await api.post("/auth/login", { email, password });
  localStorage.setItem("admin_token", data.token);
  localStorage.setItem("admin_email", data.email);
  return data;
}

export function adminLogout() {
  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_email");
}

export function adminIsAuthed() {
  return !!localStorage.getItem("admin_token");
}

export async function adminMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

// ---- Admin: photographs ----
export async function adminListPhotographs() {
  const { data } = await api.get("/admin/photographs");
  return data;
}

export async function adminCreatePhotograph(formData) {
  const { data } = await api.post("/admin/photographs", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function adminUpdatePhotograph(id, patch) {
  const { data } = await api.patch(`/admin/photographs/${id}`, patch);
  return data;
}

export async function adminReplaceImage(id, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/admin/photographs/${id}/replace-image`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function adminDeletePhotograph(id, confirm = false) {
  const { data } = await api.delete(`/admin/photographs/${id}`, {
    params: confirm ? { confirm: true } : {},
  });
  return data;
}

// ---- Admin: relationships ----
export async function adminListRelationships() {
  const { data } = await api.get("/admin/relationships");
  return data;
}

export async function adminCreateRelationship(payload) {
  const { data } = await api.post("/admin/relationships", payload);
  return data;
}

export async function adminUpdateRelationship(id, patch) {
  const { data } = await api.patch(`/admin/relationships/${id}`, patch);
  return data;
}

export async function adminDeleteRelationship(id) {
  const { data } = await api.delete(`/admin/relationships/${id}`);
  return data;
}

// ---- Admin: health + submissions + password ----
export async function adminHealth() {
  const { data } = await api.get("/admin/health");
  return data;
}

export async function adminListSubmissions() {
  const { data } = await api.get("/admin/contact-submissions");
  return data;
}

export async function adminMarkSubmissionRead(id, read = true) {
  const { data } = await api.patch(`/admin/contact-submissions/${id}`, null, {
    params: { read },
  });
  return data;
}

export async function adminDeleteSubmission(id) {
  const { data } = await api.delete(`/admin/contact-submissions/${id}`);
  return data;
}

export async function adminChangePassword(current_password, new_password) {
  const { data } = await api.post("/admin/change-password", {
    current_password,
    new_password,
  });
  return data;
}

// ---- Helper for image URLs. Admin-uploaded photos come back as `/api/images/{id}`
// (relative to the backend). Existing seeded photos are absolute URLs.
export function imageUrl(image) {
  if (!image) return "";
  if (image.startsWith("http://") || image.startsWith("https://")) return image;
  return `${BASE}${image}`;
}
